import styled, { useTheme } from 'styled-components';
import { AxisDomain } from 'recharts/types/util/types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Skeleton from 'react-loading-skeleton';
import React from 'react';
import { GraphComponentProps, YAxisRangeStartType } from '../chart.types';
import { useMatchMedia } from '@make-software/cspr-design';
import { GraphTooltip } from '../graph-tooltip';

const yAxisRangeDataType = {
  [YAxisRangeStartType.Zero]: [0, 'dataMax'],
  [YAxisRangeStartType.MinValue]: ['dataMin', 'dataMax'],
};

const ChartContainer = styled.div<{ multipleGraphChart?: boolean }>(
  ({ theme, multipleGraphChart }) =>
    theme.withMedia({
      display: 'flex',
      width: '100%',
      padding: multipleGraphChart
        ? ['32px 0 ', '40px 0', '40px 0']
        : '40px 0 0 0',
      fontSize: '10px',
    })
);

export const BarChartComponent = ({
  graphData,
  loading,
  yAxisValueKey,
  yAxisTickFormatter,
  yAxisTick,
  tooltipFormatter,
  xAxisTickFormatter,
  axisLine = false,
  yAxisRangeStart = YAxisRangeStartType.Zero,
  description,
  title,
}: GraphComponentProps) => {
  const theme = useTheme();
  const responsiveSkeletonWidth = useMatchMedia([330, 664, 872, 1136], [theme]);

  const graphAxisData: Record<string, any> = graphData.reduce((acc, value) => {
    if (!value.data) {
      return acc;
    }
    value.data.forEach((dataItem) => {
      // derive values for y-axis
      if (!acc[dataItem[yAxisValueKey]]) {
        acc[dataItem[yAxisValueKey]] = { name: dataItem[yAxisValueKey] };
      }

      // derive values for x-axis
      acc[dataItem[yAxisValueKey]][value.graphKey] =
        dataItem[value.xAxisValueKey];
    });
    return acc;
  }, {});

  const data = Object.values(graphAxisData);

  const compareByHighestValueForKey = (a, b) => {
    const getArrayOfValuesByKeys = (obj) =>
      (obj.data ?? []).map((item) => item[obj.xAxisValueKey]);

    const arrayA = getArrayOfValuesByKeys(a);
    const arrayB = getArrayOfValuesByKeys(b);

    const highestValueA = Math.max(...arrayA);
    const highestValueB = Math.max(...arrayB);
    return highestValueB - highestValueA;
  };

  //sorting the data in descending order to render areas from the area with the highest value to the area with the lowest value to prevent cropping of parts of the graphs
  const graphDataDesc = Array.from(graphData).sort(compareByHighestValueForKey);

  const graphDataLengthEmpty = graphData.length === 0;
  if (graphDataLengthEmpty) {
    return <ChartContainer>No data.</ChartContainer>;
  }

  const yAxisStartOptions = yAxisRangeDataType[yAxisRangeStart] as AxisDomain;

  return (
    <ChartContainer>
      <ResponsiveContainer width={'100%'} height={400}>
        {loading ? (
          <>
            <Skeleton width={responsiveSkeletonWidth} height={400} />
          </>
        ) : (
          <BarChart
            data={data}
            style={{ maxHeight: '70vh', aspectRatio: 1.618 }}
            width={500}
            height={400}
            title={title}
            desc={description}
          >
            <CartesianGrid
              vertical={false}
              stroke={theme.styleguideColors.borderPrimary}
            />
            <XAxis
              dataKey="name"
              tickSize={0}
              tickMargin={10}
              minTickGap={69}
              axisLine={axisLine}
              interval="preserveEnd"
              tickFormatter={xAxisTickFormatter}
              stroke={theme.styleguideColors.contentSecondary}
            />
            <YAxis
              dataKey={graphData[0].graphKey}
              tickSize={0}
              tickMargin={12}
              minTickGap={35}
              axisLine={axisLine}
              tick={yAxisTick}
              type="number"
              interval="preserveEnd"
              tickFormatter={yAxisTickFormatter}
              domain={yAxisStartOptions}
              stroke={theme.styleguideColors.contentSecondary}
            />
            <RechartsTooltip
              content={
                <GraphTooltip format={tooltipFormatter || yAxisTickFormatter} />
              }
            />
            {graphDataDesc.map((item) => (
              <Bar
                key={item.graphKey}
                dataKey={item.graphKey}
                barSize={40}
                fill={theme.styleguideColors.contentLightBlue}
                fillOpacity={1}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </ChartContainer>
  );
};
